import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "../axios_config";
import { BASE_API_URL } from "../common/endpoints";
import { describeError } from "../common/utils/error_message";
import { showAppErrorToast } from "../common/utils/app_toast";
import { QUESTION_PLACE } from "../common/constants/question";
import { buildQuestionPayload, QUESTION_DEFAULTS } from "../common/utils/build_question_payload";
import {
	clearPendingQuestion,
	PENDING_QUESTION_EVENT,
	peekPendingQuestion,
} from "../common/utils/pending_question";
import {
	getDraftKey,
	writeDraft,
} from "../common/components/modal_windows/ask_question_modal_window/question_draft";
import { useUserDataContext } from "../user_data_context";

/** Sends the question a guest wrote on the landing as soon as they are signed in. */
export const useSendPendingQuestion = () => {
	const { t } = useTranslation();
	const { userData } = useUserDataContext();
	const [sentOpen, setSentOpen] = useState(false);
	const [retryOpen, setRetryOpen] = useState(false);
	const userId = userData?._id as string | undefined;
	// One request at a time: StrictMode, a profile refresh and the save event may all call in.
	const sendingRef = useRef(false);

	const send = useCallback(async () => {
		if (!userId || sendingRef.current) return;
		// Kept in storage until the server has it: a tab closed mid-request sends it next time.
		const question = peekPendingQuestion();
		if (!question) return;
		sendingRef.current = true;
		try {
			await axios.post(
				`${BASE_API_URL}/v1/questions`,
				buildQuestionPayload({
					draft: {
						...QUESTION_DEFAULTS,
						question_text: question.text,
						question_bible_verses: question.verses,
					},
					author: userData,
					place: QUESTION_PLACE.HOME,
				}),
			);
			clearPendingQuestion();
			// The same "question sent" window, with confetti, as the ask-question modal — not a toast.
			setSentOpen(true);
		} catch (error) {
			console.error("Failed to send the pending question", describeError(error));
			// Nothing is lost: the ask-question modal opens with this text and these places.
			clearPendingQuestion();
			writeDraft(getDraftKey(QUESTION_PLACE.HOME), question, userId);
			showAppErrorToast(t("pending-question.failed"), { autoClose: 6000 });
			setRetryOpen(true);
		} finally {
			sendingRef.current = false;
		}
	}, [userId, userData, t]);

	useEffect(() => {
		send();
		window.addEventListener(PENDING_QUESTION_EVENT, send);
		return () => window.removeEventListener(PENDING_QUESTION_EVENT, send);
	}, [send]);

	return {
		sentOpen,
		closeSent: () => setSentOpen(false),
		retryOpen,
		closeRetry: () => setRetryOpen(false),
	};
};
